import apiClient, {
  type AxiosRequestConfig,
  type AxiosResponse,
} from "@shared/services/apiClient";

export interface EegPlaybackPrefetchRequest {
  playheadMs: number;
  speed: number;
  direction?: "forward" | "backward";
  viewportPx?: number;
  channels?: string[];
}

export interface EegClinicalExportRequest {
  format: "edf" | "edf_plus" | "csv" | "tsv";
  includeVideo?: boolean;
  includeMetadata?: boolean;
  reviewContext?: {
    reviewMode: "clinical" | "research";
    montageId?: string | null;
    montageName?: string | null;
    highPassFilter?: number | null;
    lowPassFilter?: number | null;
    notchFilter60Hz?: boolean | null;
    notchFilter50Hz?: boolean | null;
    sensitivity?: number | null;
    timeScale?: number | null;
    gridEnabled?: boolean;
    scaleMarkersEnabled?: boolean;
    displayProfileId?: string | null;
    displayProfileName?: string | null;
  };
}

export interface EegPlaybackManifestSegment {
  segmentId: string;
  sequenceIndex: number;
  sourceFilename: string;
  sourceRelativePath: string | null;
  segmentStartDatetime: string;
  segmentEndDatetime: string;
  durationMs: number;
  cumulativeStartMs: number;
  cumulativeEndMs: number;
  gapBeforeMs?: number;
  overlapBeforeMs?: number;
  sampleCount?: number | null;
}

export interface EegPlaybackManifest {
  recordingId: string;
  sampleRateHz: number;
  durationMs: number;
  logicalDurationMs: number;
  channels: string[];
  lodLevels: Array<{
    level: number;
    samplesPerPixel: number;
    tileMs: number;
  }>;
  totalSegments: number;
  segments: EegPlaybackManifestSegment[];
  playbackReady: boolean;
}

interface PlaybackTokenResponseShape {
  token?: string;
  expiresAt?: string;
  expiresInSeconds?: number;
  data?: {
    token?: string;
    expiresAt?: string;
    expiresInSeconds?: number;
  };
}

interface PlaybackTokenCacheEntry {
  token: string;
  expiresAtMs: number;
  refreshBlockedUntilMs: number;
  inflight?: Promise<string | null>;
}

const playbackTokenCache = new Map<string, PlaybackTokenCacheEntry>();
const PLAYBACK_TOKEN_REFRESH_SKEW_MS = 15_000;
const PLAYBACK_TOKEN_FAIL_RETRY_MS = 30_000;

function parsePlaybackTokenResponse(
  payload: unknown,
): { token: string; expiresAtMs: number } | null {
  if (!payload || typeof payload !== "object") return null;
  const body = payload as PlaybackTokenResponseShape;
  const token =
    typeof body.data?.token === "string"
      ? body.data.token
      : typeof body.token === "string"
        ? body.token
        : "";
  if (!token) return null;

  const expiresAtRaw =
    typeof body.data?.expiresAt === "string"
      ? body.data.expiresAt
      : typeof body.expiresAt === "string"
        ? body.expiresAt
        : "";
  const expiresInSecondsRaw =
    typeof body.data?.expiresInSeconds === "number"
      ? body.data.expiresInSeconds
      : typeof body.expiresInSeconds === "number"
        ? body.expiresInSeconds
        : null;

  const expiresAtMs = Number.isFinite(Date.parse(expiresAtRaw))
    ? Date.parse(expiresAtRaw)
    : Number.isFinite(Number(expiresInSecondsRaw)) &&
        Number(expiresInSecondsRaw) > 0
      ? Date.now() + Number(expiresInSecondsRaw) * 1000
      : Date.now() + 90_000;

  return {
    token,
    expiresAtMs: Math.max(Date.now() + 15_000, expiresAtMs),
  };
}

async function ensurePlaybackAccessToken(
  recordingId: string,
): Promise<string | null> {
  const key = String(recordingId || "").trim();
  if (!key) return null;

  const now = Date.now();
  const cached = playbackTokenCache.get(key);
  if (
    cached?.token &&
    now + PLAYBACK_TOKEN_REFRESH_SKEW_MS < cached.expiresAtMs
  ) {
    return cached.token;
  }
  if (cached?.inflight) {
    return cached.inflight;
  }
  if ((cached?.refreshBlockedUntilMs || 0) > now) {
    return cached?.token || null;
  }

  const inflight = (async () => {
    try {
      const response = await apiClient.post(
        `/eeg/recordings/${key}/playback-token`,
        {},
        { timeout: 5000 },
      );
      const parsed = parsePlaybackTokenResponse(response.data);
      if (!parsed) {
        const existing = playbackTokenCache.get(key);
        playbackTokenCache.set(key, {
          token: existing?.token || "",
          expiresAtMs: existing?.expiresAtMs || 0,
          refreshBlockedUntilMs: Date.now() + PLAYBACK_TOKEN_FAIL_RETRY_MS,
        });
        return existing?.token || null;
      }

      playbackTokenCache.set(key, {
        token: parsed.token,
        expiresAtMs: parsed.expiresAtMs,
        refreshBlockedUntilMs: 0,
      });
      return parsed.token;
    } catch {
      const existing = playbackTokenCache.get(key);
      playbackTokenCache.set(key, {
        token: existing?.token || "",
        expiresAtMs: existing?.expiresAtMs || 0,
        refreshBlockedUntilMs: Date.now() + PLAYBACK_TOKEN_FAIL_RETRY_MS,
      });
      return existing?.token || null;
    } finally {
      const latest = playbackTokenCache.get(key);
      if (latest) {
        delete latest.inflight;
        playbackTokenCache.set(key, latest);
      }
    }
  })();

  playbackTokenCache.set(key, {
    token: cached?.token || "",
    expiresAtMs: cached?.expiresAtMs || 0,
    refreshBlockedUntilMs: cached?.refreshBlockedUntilMs || 0,
    inflight,
  });

  return inflight;
}

export async function prewarmEegPlaybackAccessToken(
  recordingId: string,
): Promise<void> {
  await ensurePlaybackAccessToken(recordingId);
}

async function withPlaybackTokenConfig(
  recordingId: string,
  config?: AxiosRequestConfig,
): Promise<AxiosRequestConfig | undefined> {
  const playbackToken = await ensurePlaybackAccessToken(recordingId);
  if (!playbackToken) return config;
  return {
    ...(config || {}),
    headers: {
      ...(config?.headers || {}),
      "X-Playback-Token": playbackToken,
    },
  };
}

export async function getEegPlaybackManifest(
  recordingId: string,
): Promise<AxiosResponse<{ data?: EegPlaybackManifest }>> {
  const requestConfig = await withPlaybackTokenConfig(recordingId);
  return apiClient.get<{ data?: EegPlaybackManifest }>(
    `/eeg/recordings/${recordingId}/manifest`,
    requestConfig,
  );
}

export async function getEegPlaybackTiles(
  recordingId: string,
  params: {
    startMs: number;
    endMs: number;
    level?: number;
    channels?: string[];
    speed?: number;
    transportMode?: "play" | "inspect";
    requestOwner?: string;
  },
  config?: AxiosRequestConfig,
) {
  const query = new URLSearchParams();
  query.set("startMs", String(params.startMs));
  query.set("endMs", String(params.endMs));
  query.set("level", String(params.level ?? 0));
  if (typeof params.speed === "number" && Number.isFinite(params.speed)) {
    query.set("speed", String(params.speed));
  }
  if (params.transportMode === "play" || params.transportMode === "inspect") {
    query.set("transportMode", params.transportMode);
  }
  if (params.requestOwner) {
    query.set("requestOwner", params.requestOwner);
  }
  if (params.channels && params.channels.length > 0) {
    query.set("channels", params.channels.join(","));
  }

  const requestConfig = await withPlaybackTokenConfig(recordingId, config);
  return apiClient.get(
    `/eeg/recordings/${recordingId}/tiles?${query.toString()}`,
    requestConfig,
  );
}

export async function postEegPlaybackPrefetch(
  recordingId: string,
  payload: EegPlaybackPrefetchRequest,
  config?: AxiosRequestConfig,
) {
  const requestConfig = await withPlaybackTokenConfig(recordingId, config);
  return apiClient.post(
    `/eeg/recordings/${recordingId}/prefetch`,
    payload,
    requestConfig,
  );
}

export function postEegClinicalExport(
  recordingId: string,
  payload: EegClinicalExportRequest,
  config?: AxiosRequestConfig,
) {
  return apiClient.post(
    `/eeg/recordings/${recordingId}/export`,
    payload,
    config,
  );
}
